import { Pause, Play } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useMorningBrief } from '../../../lib/brief';
import { formatBriefTime } from '../../../lib/use-maestro-brief';
import { useTranslation } from '../../../lib/i18n';
import {
  alignUnits,
  alignedOffset,
  followOffset,
  positionFromGlobalOffset,
  speechDurationEstimate,
  timingUnits,
} from '../../../lib/speech-script';
import type { BriefAudio } from '../../../lib/use-brief-audio';
import { useBriefScript } from '../../../lib/use-brief-script';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { Modal } from '../../Modal';
import { BriefScriptText } from './BriefScriptText';
import { Spinner } from '../../Spinner';
import { IconButton, ScrollArea } from '../ui';
import styles from './BriefListenModal.module.css';

/**
 * The brief being read aloud, with a marker on the words as they are spoken.
 *
 * **Why this exists.** A brief runs well over a minute of continuous speech, and a listener who looks
 * away for a moment has nowhere to look back to — audio has no position you can see. Showing the script
 * with the voice's place in it means listening and reading are one act rather than two.
 *
 * The position is an ESTIMATE and is deliberately shown at word/sentence granularity: no synthesizer we
 * use returns alignment data, so it comes from mapping the audio clock onto cumulative character weight
 * (see `speech-script.ts`). It lands within a word or two, which is what "don't lose me" needs.
 */
/** Past this much of the viewport, the line the voice is on is too low to keep reading comfortably. */
const SCROLL_COMFORT_FRACTION = 0.72;
/** Where a line lands after a scroll — high enough that several sentences pass before the next one. */
const SCROLL_REST_FRACTION = 0.3;

export function BriefListenModal({ open, onClose, audio }: { open: boolean; onClose: () => void; audio: BriefAudio }) {
  const { t } = useTranslation();
  const { logModalActionBi } = useGenericBi();
  const brief = useMorningBrief();
  const script = useBriefScript(brief?.conversationId, open);
  useLoadBi('brief-script-data', { isLoading: script.isLoading, isError: script.isError }, { enabled: open });

  const segments = script.data?.segments ?? [];
  const duration = speechDurationEstimate({
    segments,
    bufferedSeconds: audio.bufferedSeconds,
    streamEnded: audio.streamEnded,
  });
  // Where each PHRASE really falls, measured from the audio's own silences — the character estimate is only
  // the prior. See `timingUnits`: sentences alone are too coarse an anchor (a 115-character sentence drifted
  // by a second in the middle), and the audio offers a pause per phrase because synthesizers breathe at
  // commas too.
  const units = timingUnits(segments);
  const spans = alignUnits(units, audio.silences, duration);
  // Nothing is marked until sound exists: a highlight sitting on sentence one through the ~1.5s synthesis
  // wait would claim the voice had started. Once it has, the marker stays put even after the brief ends —
  // a finished script that repaints as unread reads as "none of this was read".
  const started = audio.elapsedSeconds > 0 || audio.state === 'playing' || audio.state === 'paused';
  // The high-water mark: re-alignment can compute a LOWER position than a frame ago, and a trail that
  // retreats three times a second is what reads as bumpiness. Reset when a new brief starts, never during.
  // The DRAWN offset follows the measured one rather than snapping to it — a correction at an anchor glides
  // in over a quarter-second instead of colouring a sentence at once. Monotonic, and reset per brief.
  const drawn = useRef(0);
  const drawnAt = useRef(0);
  if (!started) {
    drawn.current = 0;
    drawnAt.current = 0;
  } else {
    const target = Math.max(drawn.current, alignedOffset(audio.elapsedSeconds, spans, units, audio.silences));
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const delta = drawnAt.current === 0 ? 0 : (now - drawnAt.current) / 1000;
    drawnAt.current = now;
    drawn.current = followOffset(drawn.current, target, delta);
  }
  const position = started
    ? positionFromGlobalOffset(segments, drawn.current)
    : { index: -1, charactersRead: 0, charactersReadExact: 0 };

  const bodyRef = useRef<HTMLDivElement | null>(null);
  // Carries `--top-fade`, the live scrollTop that grows the top fade. Held on the reader so the fade
  // overlay (its sibling) inherits it.
  const readerRef = useRef<HTMLDivElement | null>(null);
  // The top fade is what the read text has scrolled PAST, so it tracks scrollTop — zero at the top (the
  // opening line is fully inked before it is read), growing only as already-read lines rise under the
  // header. The CSS caps it at ~2 lines. Set straight on the node so a scroll doesn't re-render the tree.
  const onBodyScroll = (e: React.UIEvent<HTMLElement>) => {
    readerRef.current?.style.setProperty('--top-fade', `${e.currentTarget.scrollTop}px`);
  };
  // The marker's edge, so the script can be kept in view. Attached INSIDE `BriefScriptText` — when that
  // component was extracted this ref briefly pointed at nothing, and the auto-scroll silently stopped.
  const edgeRef = useRef<HTMLSpanElement | null>(null);
  // **A teleprompter, not a re-centring.** Measured on a screen recording of the native build: centring on
  // every sentence scrolled down, back up, then down again inside a second and a half, because the sentence
  // index can compute lower after a re-alignment. Text jumping under the marker is what reads as bumpiness.
  // So: never backwards, and only once the line has reached the bottom of the comfortable band.
  const lastScrolled = useRef(-1);
  useEffect(() => {
    const body = bodyRef.current;
    const edge = edgeRef.current;
    if (!body || !edge || typeof body.scrollTo !== 'function') return;
    if (position.index <= lastScrolled.current) return;
    lastScrolled.current = position.index;

    const top = edge.offsetTop;
    const bottom = top + edge.offsetHeight;
    const comfortable = body.scrollTop + body.clientHeight * SCROLL_COMFORT_FRACTION;
    if (top >= body.scrollTop && bottom <= comfortable) return; // already where the eye is
    // Hand-rolled rather than `scrollIntoView`, which also scrolls every scrollable ancestor — including
    // the page behind the scrim.
    body.scrollTo({ top: top - body.clientHeight * SCROLL_REST_FRACTION, behavior: 'smooth' });
  }, [position.index]);

  const paused = audio.state === 'paused';
  const running = audio.state === 'playing' || paused;
  // Playing shows the quiet outlined pause; paused/idle shows the filled play, disabled until there is
  // audio to resume. Mirrors iOS `BriefListenSheet.playControl`.
  const showPause = running && !paused;

  // The modal's ✕ / scrim / Escape are the only way out now, and dismissing stops the audio at the call
  // site (`BriefListenButton.close`). The stop BI is logged here so it survives dropping the separate
  // stop button — native reports it the same way (the sheet's dismiss stops the read-along).
  const handleClose = () => {
    logModalActionBi({ page_name: 'catch_up', modal_name: 'brief_listen', action: 'stop' });
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title={t('brief.title', { time: formatBriefTime(brief?.generatedDate ?? '') })} width={560} labelledBy="brief-listen-title">
      {/* The script fills the reader; a short fade dissolves lines scrolling up under the header and the
          single control floats over a fade at the bottom, so the last lines dissolve under it rather than
          being fenced off by a footer row (Figma 13053-64680 / -65004). */}
      <div className={styles.reader} ref={readerRef}>
        <ScrollArea className={styles.body} ref={bodyRef} onScroll={onBodyScroll}>
          {script.isLoading && (
            <div className={styles.state}>
              <Spinner size={16} />
            </div>
          )}
          {script.isError && <div className={styles.state}>{t('brief.listenAlong.unavailable')}</div>}
          {!script.isLoading && !script.isError && (
            <BriefScriptText
              segments={segments}
              text={script.data?.text ?? ''}
              position={position}
              edgeRef={edgeRef}
            />
          )}
        </ScrollArea>

        <div className={styles.topFade} aria-hidden />

        {/* A player's control is a SHAPE, so this is the shape and nothing else. The word lives on the
            accessible name (`aria-label`) — still there for a screen reader, localized in all 37 locales. */}
        <div className={styles.transport}>
          <IconButton
            variant={showPause ? 'outlined' : 'contained'}
            size="large"
            disabled={!running}
            aria-label={t(paused ? 'brief.resume' : 'brief.pause')}
            onClick={() => {
              logModalActionBi({ page_name: 'catch_up', modal_name: 'brief_listen', action: paused ? 'resume' : 'pause' });
              audio.togglePause();
            }}
          >
            {showPause ? (
              <Pause size={20} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
            ) : (
              <Play size={20} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
            )}
          </IconButton>
        </div>
      </div>
    </Modal>
  );
}
